<template>
  <!-- Footer Start -->
  <footer
    class="footer bg-slate-900 dark:bg-slate-800 relative text-gray-200 dark:text-gray-200"
  >
    <div class="container relative">
      <div class="grid grid-cols-12">
        <div class="col-span-12">
          <div class="py-12 md:py-[60px] px-0">
            <div class="grid md:grid-cols-12 grid-cols-1 gap-8 md:gap-6">
              <div class="lg:col-span-3 md:col-span-12">
                <NuxtLink
                  :to="$localePath('index')"
                  :aria-label="$t('footer.homeAria')"
                  class="text-[22px] focus:outline-none"
                >
                  <NuxtImg
                    src="/images/logo-light.png"
                    width="215"
                    height="28"
                    loading="lazy"
                    :alt="$t('footer.logoAlt')"
                  />
                </NuxtLink>
                <p class="mt-6 text-gray-300">
                  {{ $t("footer.slogan") }}
                </p>
                <ul class="md:hidden list-none mt-6 flex items-center justify-start gap-2.5">
                  <li v-for="link in mobileSocialLinks" :key="link.title">
                    <a
                      :href="link.href"
                      :aria-label="link.title"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex size-11 items-center justify-center rounded-md text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                      :class="SOCIAL_BG[link.title] ?? 'bg-slate-500'"
                    >
                      <Icon :name="link.icon" class="size-5" aria-hidden="true" />
                    </a>
                  </li>
                </ul>
                <ul class="hidden md:flex list-none mt-6 space-x-2">
                  <li
                    v-for="(link, index) in SOCIAL_LINKS"
                    :key="index"
                    class="inline"
                  >
                    <NuxtLink
                      :href="link.href"
                      :aria-label="link.title"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="size-11 inline-flex items-center justify-center tracking-wide align-middle text-base border border-gray-800 dark:border-gray-700 rounded-md hover:bg-red-500 hover:text-white text-slate-300"
                    >
                      <Icon
                        :name="link.icon"
                        class="size-5 align-middle"
                        size="20"
                        :title="link.title"
                      />
                    </NuxtLink>
                  </li>
                </ul>
                <!--end icon-->
              </div>
              <!--end col-->

              <div class="lg:col-span-3 md:col-span-4">
                <div class="lg:ms-8">
                  <!-- Mobile heading (unchanged) -->
                  <p
                    class="md:hidden text-[10px] font-body uppercase tracking-[0.22em] text-red-500 dark:text-red-400"
                  >
                    {{ $t("footer.headers.contacts") }}
                  </p>
                  <!-- Desktop heading: matches Info / Resort -->
                  <div class="hidden md:block">
                    <h3 class="font-display italic text-[19px] leading-none text-gray-100">
                      {{ $t("footer.headers.contacts") }}
                    </h3>
                    <span class="mt-3 block h-px w-8 bg-amber-400/70"></span>
                  </div>
                  <p class="tracking-wide text-gray-100 mt-6 md:mt-5 font-display">
                    {{ $t("subCompanyName") }}
                  </p>

                  <a
                    :href="mapsHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    :aria-label="$t('nav.drawer.mapsAria')"
                    class="group flex mt-4 font-body text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <Icon
                      name="mdi-map-marker-outline"
                      class="size-4 text-red-500 me-2 mt-1 shrink-0"
                      size="20"
                    />
                    <address class="not-italic text-gray-300 group-hover:text-red-500 transition-colors">
                      Batumi st. 16, <br />
                      Chakvi, Georgia, 6200
                    </address>
                    <Icon
                      name="lucide:arrow-up-right"
                      class="size-3.5 ms-1 mt-2 self-center text-slate-500 transition-all group-hover:text-red-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      aria-hidden="true"
                    />
                  </a>

                  <div class="flex mt-4">
                    <Icon
                      name="mdi-email-outline"
                      class="size-4 text-red-500 me-2 mt-1"
                      size="20"
                    />
                    <div class="">
                      <NuxtLink
                        :to="$localePath('/contact')"
                        class="text-slate-300 hover:text-slate-400 duration-500 ease-in-out"
                        >{{ $t('footer.contact.link') }}</NuxtLink
                      >
                    </div>
                  </div>

                  <div class="flex mt-4">
                    <Icon
                      name="mdi-phone-outline"
                      class="size-4 text-red-500 me-2 mt-1"
                      size="20"
                    />
                    <div class="">
                      <a
                        href="tel:+995-505-556-117"
                        class="text-slate-300 hover:text-slate-400 duration-500 ease-in-out"
                        >+995 505-556-117</a
                      >
                    </div>
                  </div>

                  <div class="mt-4 pt-3 border-t border-slate-700/30">
                    <a
                      :href="trustStats.airbnbProfileUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-amber-400 tracking-wider uppercase transition-colors"
                    >
                      <span class="text-amber-400">★</span>
                      <span class="tabular-nums">{{ trustStats.rating.toFixed(1) }}</span>
                      <span class="opacity-40">·</span>
                      {{ $t('trust.footer.label') }}
                    </a>
                  </div>

                </div>
              </div>
              <!--end col-->

              <div class="grid grid-cols-2 gap-x-4 md:contents">
                <div class="lg:col-span-3 md:col-span-4">
                  <div class="lg:ms-8">
                    <!-- Mobile heading (unchanged) -->
                    <p
                      class="md:hidden text-[10px] font-body uppercase tracking-[0.22em] text-red-500 dark:text-red-400"
                    >
                      {{ $t("footer.headers.info") }}
                    </p>
                    <!-- Desktop heading: editorial Playfair italic + gold rule -->
                    <div class="hidden md:block">
                      <h3 class="font-display italic text-[19px] leading-none text-gray-100">
                        {{ $t("footer.headers.info") }}
                      </h3>
                      <span class="mt-3 block h-px w-8 bg-amber-400/70"></span>
                    </div>

                    <!-- Mobile list (unchanged) -->
                    <ul class="md:hidden list-none footer-list mt-4">
                      <li
                        v-for="(item, index) in footerLinks"
                        :key="`m-${index}`"
                        :class="{ 'mt-1': index !== 0 }"
                      >
                        <NuxtLink
                          :to="item.to"
                          class="group inline-flex items-center gap-1.5 py-1 text-gray-300 hover:text-white duration-300 ease-in-out"
                        >
                          <Icon
                            name="mdi-chevron-right"
                            class="text-red-500 transition-transform duration-300 group-hover:translate-x-1"
                          />
                          {{ item.label }}
                        </NuxtLink>
                      </li>
                    </ul>

                    <!-- Desktop: editorial table-of-contents -->
                    <ol class="dl-toc hidden md:block list-none mt-5">
                      <li v-for="(item, index) in footerLinks" :key="`d-${index}`">
                        <NuxtLink :to="item.to" class="dl-toc__row">
                          <span class="dl-toc__mark" aria-hidden="true"></span>
                          <span class="dl-toc__label">{{ item.label }}</span>
                          <span class="dl-toc__arrow" aria-hidden="true">→</span>
                        </NuxtLink>
                      </li>
                    </ol>
                  </div>
                </div>
                <!--end col-->

                <div class="lg:col-span-3 md:col-span-4">
                  <!-- Mobile heading (unchanged) -->
                  <p
                    class="md:hidden text-[10px] font-body uppercase tracking-[0.22em] text-red-500 dark:text-red-400"
                  >
                    {{ $t("footer.headers.resortHighlights") }}
                  </p>
                  <!-- Desktop heading: editorial Playfair italic + gold rule -->
                  <div class="hidden md:block">
                    <h3 class="font-display italic text-[19px] leading-none text-gray-100">
                      {{ $t("footer.headers.resortHighlights") }}
                    </h3>
                    <span class="mt-3 block h-px w-8 bg-amber-400/70"></span>
                  </div>

                  <!-- Mobile list (unchanged) -->
                  <ul class="md:hidden list-none footer-list mt-4">
                    <li
                      v-for="(item, index) in resortHighlights"
                      :key="`m-${index}`"
                      :class="{ 'mt-1': index !== 0 }"
                    >
                      <NuxtLink
                        :to="item.to"
                        class="group inline-flex items-center gap-1.5 py-1 text-gray-300 hover:text-white duration-300 ease-in-out"
                      >
                        <Icon
                          name="mdi-chevron-right"
                          class="text-red-500 transition-transform duration-300 group-hover:translate-x-1"
                        />
                        {{ item.label }}
                      </NuxtLink>
                    </li>
                  </ul>

                  <!-- Desktop: editorial table-of-contents -->
                  <ol class="dl-toc hidden md:block list-none mt-5">
                    <li
                      v-for="(item, index) in resortHighlights"
                      :key="`d-${index}`"
                    >
                      <NuxtLink :to="item.to" class="dl-toc__row">
                        <span class="dl-toc__mark" aria-hidden="true"></span>
                        <span class="dl-toc__label">{{ item.label }}</span>
                        <span class="dl-toc__leader" aria-hidden="true"></span>
                        <span class="dl-toc__arrow" aria-hidden="true">→</span>
                      </NuxtLink>
                    </li>
                  </ol>
                </div>
                <!--end col-->
              </div>
            </div>
            <!--end grid-->
          </div>
          <!--end col-->
        </div>
      </div>
      <!--end grid-->
    </div>
    <!--end container-->

    <div class="py-5 md:py-[30px] px-0 border-t border-gray-800 dark:border-gray-700">
      <div class="container relative text-center">
        <div class="grid grid-cols-1">
          <div class="text-center">
            <p class="mb-0 text-sm md:text-base">
              © {{ new Date().getFullYear() }}
              <NuxtLink
                :to="$localePath('index')"
                class="hover:text-white transition-colors"
              >{{ $t("footer.brandLine") }}</NuxtLink>.
              <Icon name="mdi-heart" class="text-red-600" /> by BITRU
            </p>
          </div>
        </div>
        <!--end grid-->
      </div>
      <!--end container-->
    </div>
  </footer>
  <!--end footer-->
  <!-- Footer End -->
</template>

<script setup>
import { trustStats } from "~/data/trust";

const { t } = useI18n();
const localePath = useLocalePath();

const config = useRuntimeConfig();
const SOCIAL_LINKS = useSocialLinks();

// Reception coordinates — same point used in DrawerContact.vue,
// location-routes.json and components/NativeMapsLinks.vue.
const RESORT_COORDS = "41.720341,41.728457";
const mapsHref = `https://www.google.com/maps/search/?api=1&query=${RESORT_COORDS}`;

const MOBILE_SOCIAL_ORDER = ["WhatsApp", "Telegram", "Phone", "FaceTime", "Instagram", "Facebook"];
const SOCIAL_BG = {
  WhatsApp: "bg-green-500 hover:bg-green-600",
  Telegram: "bg-sky-500 hover:bg-sky-600",
  Phone: "bg-red-500 hover:bg-red-600",
  FaceTime: "bg-emerald-500 hover:bg-emerald-600",
  Instagram:
    "bg-gradient-to-tr from-amber-400 via-pink-500 to-purple-600 hover:brightness-110",
  Facebook: "bg-blue-600 hover:bg-blue-700",
};
const mobileSocialLinks = computed(() => {
  const facetime = {
    href: `facetime:${config.public.whatsappNumber || ""}`,
    icon: "mdi-video-outline",
    title: "FaceTime",
  };
  const byTitle = new Map([...SOCIAL_LINKS, facetime].map((l) => [l.title, l]));
  return MOBILE_SOCIAL_ORDER
    .map((title) => byTitle.get(title))
    .filter((l) => Boolean(l && l.href));
});

const footerLinks = computed(() => [
  { to: localePath("/"), label: t("nav.home") },
  { to: localePath("/about"), label: t("nav.about") },
  { to: localePath("/prices"), label: t("nav.prices") },
  { to: localePath("/amenities"), label: t("nav.amenities") },
  { to: localePath("/location"), label: t("nav.location") },
  { to: localePath("/guest-services"), label: t("nav.services") },
  { to: localePath("/contact"), label: t("footer.contact.link") },
  { to: localePath("/privacy"), label: t("privacy.title") },
]);

const resortHighlights = computed(() => [
  { to: localePath("/search"), label: t("footer.resortHighlights.apartments") },
  { to: localePath("/amenities"), label: t("footer.resortHighlights.pool") },
  { to: localePath("/amenities"), label: t("footer.resortHighlights.aquapark") },
  { to: localePath("/amenities"), label: t("footer.resortHighlights.botanicGarden") },
]);
</script>

<style scoped>
/* ── Editorial table-of-contents links (desktop) ───────────────────────
   Two-column footer link lists rendered as a magazine TOC: a tabular
   index, the label, a dotted leader that wipes solid on hover, and a
   small arrow that nudges right. Replaces the generic chevron list. */

.dl-toc li + li {
  margin-top: 2px;
}

.dl-toc__row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 8px 0;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  line-height: 1.2;
  color: rgb(209 213 219);
  transition: color 240ms ease;
}

/* Editorial diamond marker — replaces numeric index.
   Pure CSS rotated square; references the gold rule under section
   headings while contrasting (vertical accent) with the horizontal
   leader line on the right. */
.dl-toc__mark {
  flex-shrink: 0;
  position: relative;
  width: 14px;
  height: 14px;
  align-self: center;
}
.dl-toc__mark::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: rgba(255, 46, 99, 0.6); /* dim brand coral */
  transition:
    background 240ms ease,
    transform 320ms cubic-bezier(0.65, 0, 0.35, 1),
    box-shadow 280ms ease;
}

.dl-toc__label {
  flex-shrink: 0;
  white-space: nowrap;
  transition: color 240ms ease;
}

.dl-toc__arrow {
  flex-shrink: 0;
  margin-left: 8px;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  line-height: 1;
  color: rgba(255, 46, 99, 0); /* hidden until hover */
  transform: translateX(-4px);
  transition:
    color 260ms ease,
    transform 320ms cubic-bezier(0.65, 0, 0.35, 1);
}

/* Hover state ── label brightens, index goes gold,
   leader becomes a solid gold hairline, arrow fades in & nudges right. */
.dl-toc__row:hover {
  color: rgb(255 255 255);
}
.dl-toc__row:hover .dl-toc__mark::before {
  background: #ff2e63;
  transform: translate(-50%, -50%) rotate(45deg) scale(1.4);
  box-shadow: 0 0 8px rgba(255, 46, 99, 0.55);
}
.dl-toc__row:hover .dl-toc__label {
  color: rgb(255 255 255);
}
.dl-toc__row:hover .dl-toc__arrow {
  color: #ff2e63;
  transform: translateX(0);
}

/* Keyboard focus mirrors hover for parity */
.dl-toc__row:focus-visible {
  outline: none;
  color: rgb(255 255 255);
}
.dl-toc__row:focus-visible .dl-toc__arrow {
  color: #ff2e63;
  transform: translateX(0);
}
.dl-toc__row:focus-visible .dl-toc__mark::before {
  background: #ff2e63;
  transform: translate(-50%, -50%) rotate(45deg) scale(1.4);
  box-shadow: 0 0 8px rgba(255, 46, 99, 0.55);
}

@media (prefers-reduced-motion: reduce) {
  .dl-toc__row,
  .dl-toc__mark::before,
  .dl-toc__label,
  .dl-toc__arrow {
    transition: none;
  }
}
</style>
